import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ClipboardList, Inbox, Loader2, Plus, RefreshCw, Wrench } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import SegmentedControl from '../../components/base/SegmentedControl';
import BrandRule from '../../components/brand/BrandRule';
import InlineAlert from '../../components/common/InlineAlert';
import { GateLoading } from '../../components/gating/FeatureGate';
import { ACCOUNT_SUSPENDED_MESSAGE_KEY, apiErrorInfo, isAccountSuspended } from '../../utils/apiErrorInfo';
import {
  listIncomingEngagements,
  listMyEngagements,
  updateEngagement,
  type ServiceEngagement,
  type ServiceEngagementStatus,
} from '../../services/serviceEngagementService';
import EngagementCard from './EngagementCard';
import GuestPreview from './GuestPreview';
import RequestServiceForm from './RequestServiceForm';

type Tab = 'mine' | 'incoming';

/**
 * «الخدمات المهنية»: request a valuation, consulting, analysis, or property or
 * facilities management from a real-estate professional, and follow it to
 * delivery.
 *
 * Public route. A guest sees what the five services are and how a request
 * moves, with a sign-in prompt (GuestPreview); nothing is fetched for them. A
 * signed-in user gets "My requests" (as a client) and "Incoming requests" (for
 * their own professional activities). The API requires sign-in on every call.
 */
export default function ServiceEngagementsPage() {
  const { t, i18n } = useTranslation(['engage', 'common']);
  const { session, user, loading: authLoading } = useAuth();
  const token = session?.token ?? null;
  const signedIn = Boolean(user && token);

  const [tab, setTab] = useState<Tab>('mine');
  const [mine, setMine] = useState<ServiceEngagement[]>([]);
  const [incoming, setIncoming] = useState<ServiceEngagement[]>([]);
  const [loading, setLoading] = useState(true);
  // i18n keys, so a language switch re-renders the messages too.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [disabled, setDisabled] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [pending, setPending] = useState<{ id: string; status: ServiceEngagementStatus } | null>(null);

  const requestButtonRef = useRef<HTMLButtonElement>(null);
  const restoreRequestFocus = useRef(false);
  const requestSeq = useRef(0);

  /** `silent` refreshes behind the current list (after an update) instead of showing the spinner. */
  const load = useCallback(
    async ({ silent = false }: { silent?: boolean } = {}) => {
      if (!token) return;
      const seq = ++requestSeq.current;
      if (!silent) {
        setLoading(true);
        setLoadError(null);
      }
      try {
        const [nextMine, nextIncoming] = await Promise.all([listMyEngagements(token), listIncomingEngagements(token)]);
        if (seq !== requestSeq.current) return;
        setMine(nextMine);
        setIncoming(nextIncoming);
        setLoadError(null);
        setDisabled(false);
      } catch (error) {
        if (seq !== requestSeq.current || silent) return;
        if (apiErrorInfo(error).status === 404) setDisabled(true);
        else if (isAccountSuspended(error)) setLoadError(ACCOUNT_SUSPENDED_MESSAGE_KEY);
        else setLoadError('errors.load');
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    },
    [token]
  );

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!formOpen && restoreRequestFocus.current) {
      restoreRequestFocus.current = false;
      requestButtonRef.current?.focus();
    }
  }, [formOpen]);

  const openForm = () => {
    setNotice(null);
    setFormOpen(true);
  };

  const closeForm = () => {
    restoreRequestFocus.current = true;
    setFormOpen(false);
  };

  const handleSent = (created: ServiceEngagement) => {
    setMine((list) => [created, ...list.filter((item) => item.id !== created.id)]);
    setTab('mine');
    setNotice('form.sent');
    closeForm();
    // Sent before the first load finished: that load is superseded, so this one
    // must be able to show its own spinner and error.
    void load({ silent: !loading });
  };

  const update = useCallback(
    async (engagement: ServiceEngagement, status: ServiceEngagementStatus, deliverableUrl?: string) => {
      if (!token) return 'errors.session';
      setPending({ id: engagement.id, status });
      setNotice(null);
      try {
        const updated = await updateEngagement(token, engagement.id, {
          status,
          ...(deliverableUrl ? { deliverableUrl } : {}),
        });
        // The API answers with the updated row: show it at once, then refresh
        // both lists behind it.
        if (updated?.id) {
          const merge = (list: ServiceEngagement[]) =>
            list.map((item) => (item.id === updated.id ? { ...item, ...updated } : item));
          setMine(merge);
          setIncoming(merge);
        }
        setNotice('card.updated');
        void load({ silent: true });
        return null;
      } catch (error) {
        const { status: http } = apiErrorInfo(error);
        if (http === 401) return 'errors.session';
        if (isAccountSuspended(error)) return ACCOUNT_SUSPENDED_MESSAGE_KEY;
        if (http === 403) return 'errors.forbidden';
        if (http === 404) return 'errors.notFound';
        return 'errors.update';
      } finally {
        setPending(null);
      }
    },
    [token, load]
  );

  const countsKnown = !loading && !loadError;
  const tabLabel = (key: string, count: number) =>
    countsKnown ? t('tabs.withCount', { label: t(key), n: count }) : t(key);
  const list = tab === 'mine' ? mine : incoming;

  // The menu links here only while the feature is on. A guest who arrives by URL
  // while it is off is told so, not invited to sign in to something unavailable.
  // (Signed-in users learn it from the API, which 404s while it is off.)
  const workflowsEnabled = import.meta.env.VITE_SERVICE_WORKFLOWS_ENABLED === 'true';
  const switchedOff = (
    <p className="mt-8 rounded-2xl border border-[var(--pv-border-soft)] bg-[var(--pv-color-surface-soft)] px-5 py-10 text-center text-[0.9375rem] text-[var(--pv-color-text-muted)]">
      {t('errors.disabled')}
    </p>
  );

  let body: ReactNode;
  if (authLoading) {
    body = <GateLoading />;
  } else if (!signedIn) {
    body = workflowsEnabled ? <GuestPreview /> : switchedOff;
  } else if (disabled) {
    body = switchedOff;
  } else {
    body = (
      <>
        {/* Always mounted while signed in, so screen readers announce each confirmation. */}
        <div role="status" aria-live="polite">
          {notice ? (
            <InlineAlert tone="success" className="mt-6">
              {t(notice)}
            </InlineAlert>
          ) : null}
        </div>

        {formOpen && token ? <RequestServiceForm token={token} onSent={handleSent} onCancel={closeForm} /> : null}

        <div className="mt-8">
          {/* sm keeps «الطلبات الواردة (n)» on one line at 375px (two equal tabs). */}
          <SegmentedControl<Tab>
            variant="underline"
            size="sm"
            fullWidth
            value={tab}
            onChange={setTab}
            ariaLabel={t('tabs.label')}
            options={[
              { value: 'mine', label: tabLabel('tabs.mine', mine.length), icon: <ClipboardList /> },
              { value: 'incoming', label: tabLabel('tabs.incoming', incoming.length), icon: <Inbox /> },
            ]}
          />
          <div className="mt-4">
            {loading ? (
              <p
                role="status"
                className="flex items-center justify-center gap-2 py-12 text-[0.9375rem] text-[var(--pv-color-text-muted)]"
              >
                <Loader2
                  className="h-[1.125rem] w-[1.125rem] shrink-0 animate-spin text-[var(--pv-color-primary)] motion-reduce:animate-none"
                  aria-hidden="true"
                />
                {t('list.loading')}
              </p>
            ) : loadError ? (
              <InlineAlert
                live="alert"
                action={
                  loadError === ACCOUNT_SUSPENDED_MESSAGE_KEY ? undefined : (
                    <Button variant="secondary" size="sm" onClick={() => void load()}>
                      <RefreshCw className="h-4 w-4" aria-hidden="true" />
                      {t('errors.retry')}
                    </Button>
                  )
                }
              >
                {t(loadError)}
              </InlineAlert>
            ) : list.length === 0 ? (
              <div className="rounded-2xl border border-[var(--pv-border-soft)] bg-[var(--pv-color-surface-strong)] px-6 py-12 text-center">
                <span
                  aria-hidden="true"
                  className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-[var(--pv-color-surface-sunken)] text-[var(--pv-color-primary)] ring-1 ring-inset ring-[var(--pv-border-soft)]"
                >
                  {tab === 'mine' ? <ClipboardList className="h-6 w-6" /> : <Inbox className="h-6 w-6" />}
                </span>
                <p className="mt-4 text-base font-bold text-[var(--pv-color-text)]">
                  {tab === 'mine' ? t('list.emptyMine') : t('list.emptyIncoming')}
                </p>
              </div>
            ) : (
              <ul className="space-y-3">
                {list.map((engagement) => (
                  <EngagementCard
                    key={engagement.id}
                    engagement={engagement}
                    asProvider={tab === 'incoming'}
                    onUpdate={update}
                    pendingStatus={pending?.id === engagement.id ? pending.status : null}
                    busy={pending !== null}
                  />
                ))}
              </ul>
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <div dir={i18n.dir()} className="flex min-h-screen flex-col bg-[var(--pv-color-surface)]">
      <Navigation />
      <main id="main-content" className="flex-1">
        <div className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-6 sm:py-10">
          <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex min-w-0 items-start gap-3">
              <span
                aria-hidden="true"
                className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[var(--pv-color-surface-sunken)] text-[var(--pv-color-primary)] ring-1 ring-inset ring-[var(--pv-border-soft)]"
              >
                <Wrench className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <h1 className="text-2xl font-bold leading-tight text-[var(--pv-color-primary)]">{t('title')}</h1>
                <BrandRule className="mt-2.5" />
                <p className="mt-3 text-[0.9375rem] leading-relaxed text-[var(--pv-color-text-muted)]">{t('subtitle')}</p>
              </div>
            </div>
            {signedIn && !authLoading && !disabled && !formOpen ? (
              <Button ref={requestButtonRef} onClick={openForm} className="w-full shrink-0 sm:w-auto">
                <Plus className="h-5 w-5 shrink-0" aria-hidden="true" />
                {t('request')}
              </Button>
            ) : null}
          </header>
          {body}
        </div>
      </main>
      <Footer />
    </div>
  );
}
